import React, { useEffect, useState } from 'react';
import {
  Sparkles, ArrowRight, ArrowLeft, X, Users, Image as ImageIcon,
  LayoutDashboard, FolderKanban, Check,
} from 'lucide-react';
import { axiosInstance } from '../App';

/**
 * PilotWelcomeTour — a one-shot 4-step onboarding modal for users whose
 * role is `pilot_admin` and who have not yet completed the tour
 * (`pilot_tour_completed_at` is null on `/api/auth/me`).
 *
 * The modal:
 *   1. Welcomes the user and surfaces the size of their Pilot catalog (60 modules).
 *   2. Lists the four module categories so they can map the menu mentally.
 *   3. Recommends the first 3 concrete actions (logo, team, project).
 *   4. Confirms — calls POST /api/auth/me/pilot-tour-complete and closes.
 *
 * Keep it localized in two languages because the rest of the app is
 * trilingual; we render Arabic on top + English subtitle. Numerics stay
 * in English (matches the global locale policy already enforced).
 */
const STEPS = [
  {
    icon: Sparkles,
    color: 'from-fuchsia-500 to-indigo-500',
    title_ar: 'مرحباً بك في مساحة العمل التجريبية',
    title_en: 'Welcome to your Pilot workspace',
    body_ar:
      'تم تجهيز شركتك بـ 60 وحدة مُنسّقة من نظام ERP للمقاولات. لا توجد بيانات تجريبية — كل شيء جاهز للاستخدام الحقيقي خلال 60 يوم.',
    body_en:
      'Your tenant ships with 60 curated modules from the Construction ERP. Zero demo data — everything is yours to fill, with a 60-day Pilot window.',
  },
  {
    icon: LayoutDashboard,
    color: 'from-indigo-500 to-cyan-500',
    title_ar: 'القائمة الجانبية مقسمة إلى 4 فئات',
    title_en: 'Your sidebar groups 60 modules into 4 zones',
    bullets: [
      { ar: 'الإدارة التنفيذية والمشاريع (Dashboards, PEC, Approvals)', en: 'Executive & Projects' },
      { ar: 'العمليات التجارية (RFQ, Quotations, Customers, Invoices)', en: 'Commercial Operations' },
      { ar: 'المحاسبة والامتثال (Journal, VAT, ZATCA, Audit Trail)', en: 'Accounting & Compliance' },
      { ar: 'الإدارة الإدارية (Users, Permissions, Backup, Governance)', en: 'Admin & Governance' },
    ],
  },
  {
    icon: Users,
    color: 'from-emerald-500 to-teal-500',
    title_ar: 'أنصح بالبدء بهذه الثلاث خطوات',
    title_en: 'Three quick wins to get traction',
    bullets: [
      {
        ar: 'ارفع شعار شركتك من قائمة الشركات — يظهر تلقائياً في جميع الصفحات والمستندات',
        en: 'Upload your company logo (appears in every header & PDF)',
        icon: ImageIcon,
      },
      {
        ar: 'أضف فريق العمل (حد أقصى 25 مستخدم) واضبط صلاحياتهم من Role Permissions',
        en: 'Invite your team — up to 25 users; tune access in Role Permissions',
        icon: Users,
      },
      {
        ar: 'أنشئ أول مشروع عقد لتجريب دورة العمل الكاملة من العرض إلى الفاتورة',
        en: 'Create your first project to test the full Quote → Invoice loop',
        icon: FolderKanban,
      },
    ],
  },
  {
    icon: Check,
    color: 'from-amber-500 to-rose-500',
    title_ar: 'نافذة التجريب 60 يوم — ملاحظاتك هي الذهب',
    title_en: 'Your 60-day Pilot — your feedback shapes the roadmap',
    body_ar:
      'كل احتكاك تواجهه يُسجَّل في "تقرير فجوة الواقع" الذي يحدد ما نبنيه بعد التجربة. لا تتردد في الإبلاغ عن أي شيء مفقود أو غير منطقي.',
    body_en:
      "Every friction you hit gets logged in the Reality Gap Report and drives what we build next. Don't hold back any feedback.",
  },
];

const PilotWelcomeTour = ({ user, onClose }) => {
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);

  // Defensive: if the parent rendered us by mistake for a non-pilot user,
  // bail immediately without showing anything.
  useEffect(() => {
    if (!user || user.role !== 'pilot_admin') onClose?.();
  }, [user, onClose]);

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];
  const Icon = current.icon;

  const finish = async () => {
    if (closing) return;
    setClosing(true);
    try {
      await axiosInstance.post('/auth/me/pilot-tour-complete');
    } catch {
      // Best-effort. We close the tour either way to avoid trapping the user.
    }
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      data-testid="pilot-welcome-tour"
    >
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Skip button */}
        <button
          onClick={finish}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition"
          data-testid="pilot-tour-skip"
          aria-label="Skip tour"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header with gradient */}
        <div className={`bg-gradient-to-br ${current.color} px-6 pt-8 pb-12 text-white`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight" data-testid="pilot-tour-title-ar">
                {current.title_ar}
              </h2>
              <p className="text-xs text-white/80 mt-0.5" data-testid="pilot-tour-title-en">
                {current.title_en}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 -mt-6">
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 min-h-[180px]">
            {current.body_ar && (
              <p className="text-sm text-gray-800 leading-relaxed text-right" dir="rtl">
                {current.body_ar}
              </p>
            )}
            {current.body_en && (
              <p className="text-xs text-gray-500 mt-2 italic">{current.body_en}</p>
            )}
            {current.bullets && (
              <ul className="space-y-3 mt-1">
                {current.bullets.map((b, i) => {
                  const BulletIcon = b.icon;
                  return (
                    <li key={i} className="flex items-start gap-3" data-testid={`pilot-tour-bullet-${i}`}>
                      <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                        {BulletIcon ? (
                          <BulletIcon className="w-3.5 h-3.5" />
                        ) : (
                          <span className="text-xs font-bold">{i + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 text-right" dir="rtl">
                        <p className="text-sm text-gray-800 leading-snug">{b.ar}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5" dir="ltr">{b.en}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between py-5">
            <div className="flex gap-1.5" data-testid="pilot-tour-dots">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? 'w-8 bg-indigo-600' : 'w-1.5 bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-800 inline-flex items-center gap-1"
                  data-testid="pilot-tour-prev"
                >
                  <ArrowRight className="w-3.5 h-3.5" />
                  السابق
                </button>
              )}
              <button
                onClick={isLast ? finish : () => setStep(step + 1)}
                disabled={closing}
                className="px-4 py-2 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid={isLast ? 'pilot-tour-finish' : 'pilot-tour-next'}
              >
                {isLast ? (closing ? 'جاري الحفظ…' : 'فهمت — لنبدأ') : 'التالي'}
                {!isLast && <ArrowLeft className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PilotWelcomeTour;
